/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  DropDownComponent,
  TableComponent,
  CalendarComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadBookingVsInscanOutscanReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";

const BookingInscanOutscanReport = () => {
  // Global request cancellation hook
  const { createNewRequest, isLatestRequest } = useRequestCancellation();

  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  // Set default date range to last 7 days
  const defaultDateRange = [
    moment().subtract(1, "days"),
    moment()
  ];

  const [dateRange, setDateRange] = useState(defaultDateRange);

  // Handle date range change with validation
  const handleDateRangeChange = (dates) => {
    if (!dates || dates.length !== 2) {
      setDateRange(dates);
      setLoading(false);
      return;
    }

    const [startDate, endDate] = dates;
    const diffDays = endDate.diff(startDate, "days");

    if (diffDays > 7) {
      message.error("Date range cannot exceed 7 days");
      return;
    }

    setDateRange(dates);
  };
  const [hubList, setHubList] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [cpList, setCpList] = useState([]);
  const [selectedCps, setSelectedCps] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const statusOptions = [
    { label: "inscan", value: "inscan" },
    { label: "outscan", value: "outscan" }
  ];
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [hubUser, setHubUser] = useState();
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

  // Fetch hub list on mount
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const hubs = await getHubList();
        setHubList(hubs);
      } catch {
        message.error("Failed to fetch hub list");
      } finally {
        // setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userinfo"));
    let userid = JSON.parse(localStorage.getItem("userId"));
    setHubUser(user?.roles?.[0]);

    const activeHub = JSON.parse(localStorage.getItem("active_hub"));
    const activeHubId = activeHub?.hubId;

    if (hubList.length > 0) {
      if (activeHubId) {
        const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
        }
      } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
        const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
        if (defaultHub) {
          setSelectedHubs([defaultHub.value]);
          // Optionally, call selectHubHandle([defaultHub.value]) if you use a handler
        }
      }
    }
  }, [hubList]);

  // Fetch CP list when hubs change
  useEffect(() => {
    if (selectedHubs.length > 0) {
      Promise.all(selectedHubs.map(hubId => getHubCPList(hubId)))
        .then(cpLists => {
          const allCps = [...new Set(cpLists.flat())];
          setCpList(allCps);
        })
        .catch(() => {
          message.error("Failed to fetch CP lists");
          setCpList([]);
        });
    } else {
      setCpList([]);
    }
  }, [selectedHubs]);

  // Fetch report data when filters or pagination change
  useEffect(() => {
    // Wait for hubUser to be initialized
    if (!hubUser) {
      return;
    }
    // For HUBADMIN users, hub selection is mandatory
    if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
      return; // Don't fetch if HUBADMIN has no hub selected
    }
    fetchReport();
    // eslint-disable-next-line
  }, [hubUser, selectedHubs, selectedCps, selectedStatus, dateRange, pagination.current, pagination.pageSize]);

  const fetchReport = async () => {
    // For HUBADMIN users, hub selection is mandatory
    if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedHubs.length === 0) {
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
      return;
    }

    // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
    const { signal, requestId } = createNewRequest("booking-inscan-outscan-report");

    try {
      setLoading(true);
      setReportData([]); // Clear previous data

      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const hasValidDates = Array.isArray(dateRange) && dateRange[0] && dateRange[1] && typeof dateRange[0].format === "function" && typeof dateRange[1].format === "function";

      const params = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        ...(hasValidDates && {
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD")
        }),
        audit: selectedStatus.length > 0 ? selectedStatus : [],
        page: pagination.current,
        pageSize: pagination.pageSize
      };

      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getBookingVsInscanOutscanReport(params, { signal });

      // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        console.log(`🔥 Request ${requestId} completed successfully - updating state`);
        setReportData(data);
        setLastsynced(lastRefreshedAt);
        setNextScheduledRefresh(nextScheduledRefresh);
        setPagination(prev => ({
          ...prev,
          total,
          current: page,
          pageSize
        }));
      }

    } catch (error) {
      // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
      if (error.name === "AbortError") {
        console.log("🔥 Request was cancelled at HTTP level - no state update");
        return;
      }

      // Only update if this is still the latest request
      if (isLatestRequest(requestId)) {
        console.error("Error fetching booking vs first inscan report:", error);
        message.error("Failed to fetch report data");
        setReportData([]);
        setPagination(prev => ({
          ...prev,
          total: 0
        }));
      }
    } finally {
      // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
      if (isLatestRequest(requestId)) {
        setLoading(false);
      }
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a date range for download");
      return;
    }

    setDownloadLoading(true);
    try {
      const selectedHubData = hubList.filter(hub => selectedHubs?.includes(hub.value));
      const selectedCpData = cpList.filter(cp => selectedCps?.includes(cp.value));

      const downloadParams = {
        ...(selectedHubData?.length > 0 && { hubNames: selectedHubData.map(hub => hub.label) }),
        ...(selectedCpData?.length > 0 && { cpNames: selectedCpData.map(cp => cp.label) }),
        startDate: dateRange[0].format("YYYY-MM-DD"),
        endDate: dateRange[1].format("YYYY-MM-DD"),
        audit: selectedStatus.length > 0 ? selectedStatus : []
      };

      await downloadBookingVsInscanOutscanReport(downloadParams);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  // Table columns (customize as needed)
  const columns = [
    {
      title: "No.",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    { title: "Date", dataIndex: "date", key: "date" },
    {
      title: "AWB Number", dataIndex: "awbNumber", key: "awbNumber", sorter: true,
      render: (text, record) => (
        <span
          style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
          onClick={() => {
            const url = `/shipment-tracker?awb=${text}`;
            window.open(url, "_blank");
          }}
        >
          {text}
        </span>
      )
    },
    { title: "Booking CP", dataIndex: "bookingCp", key: "bookingCp", width: 200 },
    { title: "To Hub", dataIndex: "toHub", key: "toHub", width: 200 },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Service Type", dataIndex: "serviceType", key: "serviceType" },
    { title: "Status", dataIndex: "status", key: "status", width: 80 },
    { title: "Travel By", dataIndex: "travelBy", key: "travelBy" },
    { title: "Zone", dataIndex: "zone", key: "zone" },
  ];

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Booking vs First Inscan Report">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp
              lastRefreshedAt={lastsynced}
              nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">From & To Date</div>
            <CalendarComponent
              className="custom-calendar"
              type="range"
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">To hub</div>
            <DropDownComponent
              placeholder="To hub"
              options={hubList}
              onChange={setSelectedHubs}
              value={selectedHubs}
              allowClear
              mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
              disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Booking Cp</div>
            <DropDownComponent
              placeholder="Booking Cp"
              options={cpList}
              onChange={setSelectedCps}
              value={selectedCps}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">Status</div>
            <DropDownComponent
              placeholder="Status"
              options={statusOptions}
              onChange={setSelectedStatus}
              value={selectedStatus}
              allowClear
              mode="multiple"
              maxTagCount="responsive"
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={24} style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '20px' }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              loading={downloadLoading}
              disabled={!dateRange || dateRange.length !== 2}
            >
              Download Report
            </Button>
          </Col>

        </Row>
      </Card>
      <TableComponent
        columns={columns}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
        sticky
      />
    </div>
  );
};

export default BookingInscanOutscanReport; 
